/**
 * DocumentInfoPanel — read-only knowledge view for the selected document.
 *
 * Entirely data-driven: everything rendered comes from the definition
 * object and the schema's CAPABILITIES list. No document name or capability
 * is hardcoded here — new documents and new capabilities appear without
 * touching this component.
 */
import React from 'react';
import { BookOpen, Ruler, ListOrdered, CheckCircle2 } from 'lucide-react';
import { CAPABILITIES } from '../documentKnowledge';

const DocumentInfoPanel = ({ definition }) => {
  if (!definition) return null;

  const supports = CAPABILITIES.filter(({ key }) => definition[key]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="document-info-panel">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-purple-500" /> {definition.name}
        </h3>
        <span className="text-[11px] text-gray-400 flex items-center gap-1 whitespace-nowrap">
          <Ruler className="w-3 h-3" />
          {definition.recommendedSizes[0].name} · {definition.supportedOrientations.join(' / ')}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-4">{definition.description}</p>

      <div className="space-y-4">
        <div>
          <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Purpose</h4>
          <p className="text-sm text-gray-800">{definition.purpose}</p>
        </div>

        <div>
          <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Supports</h4>
          <div className="flex flex-wrap gap-1.5">
            {supports.map(({ key, label }) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 px-2 py-0.5 text-[11px]"
              >
                <CheckCircle2 className="w-3 h-3" /> {label}
              </span>
            ))}
            {supports.length === 0 && (
              <span className="text-xs text-gray-400">Minimal document — no rich content blocks.</span>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <ListOrdered className="w-3 h-3" /> Typical Structure
          </h4>
          <ol className="space-y-1">
            {definition.typicalSections.map((section, i) => (
              <li key={section} className="flex items-center gap-2 text-sm text-gray-700">
                <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-500 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </span>
                {section}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default DocumentInfoPanel;
